// CallExtras — Sanoire extensions layered over the LiveKit VideoConference:
// live captions (browser STT → data channel), emoji reactions, raise hand,
// background blur, recording (Egress), persisted in-call chat, and an AI
// meeting-notes summary generated when the call ends. Everything is wrapped in
// try/catch so it can never crash the call; unconfigured features degrade quietly.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { Track, RoomEvent } from 'livekit-client';

const TXT = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const REACTIONS = ['👍', '❤️', '😂', '🎉', '👏', '🙌'];
const SVG = (p) => ({ width: 17, height: 17, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', ...p });
const IconSmile = () => (<svg {...SVG()}><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>);
const IconHand = () => (<svg {...SVG()}><path d="M18 11V6a2 2 0 0 0-4 0" /><path d="M14 10V4a2 2 0 0 0-4 0v2" /><path d="M10 10.5V6a2 2 0 0 0-4 0v8" /><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" /></svg>);
const IconDots = () => (<svg {...SVG()}><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>);
const IconMicOff = () => (<svg {...SVG({ width: 15, height: 15 })}><line x1="1" y1="1" x2="23" y2="23" /><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" /><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" /><line x1="12" y1="19" x2="12" y2="23" /></svg>);
const IconPoll = () => (<svg {...SVG({ width: 15, height: 15 })}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>);
const IconGrid = () => (<svg {...SVG({ width: 15, height: 15 })}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></svg>);
const IconSparkle = () => (<svg {...SVG({ width: 15, height: 15 })}><path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" /><path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z" /></svg>);
const IconImage = () => (<svg {...SVG({ width: 15, height: 15 })}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>);
const IconUserPlus = () => (<svg {...SVG({ width: 15, height: 15 })}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="16" y1="11" x2="22" y2="11" /></svg>);
const IconClock = () => (<svg {...SVG({ width: 14, height: 14 })}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>);
const SANOIRE_BG = (() => { try { const c = document.createElement('canvas'); c.width = 1280; c.height = 720; const x = c.getContext('2d'); const g = x.createLinearGradient(0, 0, 0, 720); g.addColorStop(0, '#E8EEF2'); g.addColorStop(0.5, '#F2EEE7'); g.addColorStop(1, '#EDE8E0'); x.fillStyle = g; x.fillRect(0, 0, 1280, 720); return c.toDataURL('image/png'); } catch (e) { return ''; } })();
const enc = new TextEncoder();
const dec = new TextDecoder();

function publish(room, topic, obj) {
  try { room.localParticipant.publishData(enc.encode(JSON.stringify(obj)), { reliable: true, topic }); } catch (_e) {}
}

export default function CallExtras({ roomName, sessionId, me, supabase, mainRoom, activeRoom, onSwitchRoom }) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const myName = (me && (me.name || me.initials)) || 'Member';
  const myInit = (me && me.initials) || (myName.slice(0, 2).toUpperCase());

  const [ccOn, setCcOn] = useState(false);
  const [captions, setCaptions] = useState([]);      // [{init,name,text,ts}]
  const [reacts, setReacts] = useState([]);          // floating {id,emoji,name}
  const [hands, setHands] = useState({});            // {init: name}
  const [handUp, setHandUp] = useState(false);
  const [camFx, setCamFx] = useState('none'); // none | blur | bg
  const [noiseOn, setNoiseOn] = useState(false);
  const [invitePanel, setInvitePanel] = useState(false);
  const [inviteMembers, setInviteMembers] = useState([]);
  const [inviteSent, setInviteSent] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [recOn, setRecOn] = useState(false);
  const [recBusy, setRecBusy] = useState(false);
  const [pickReact, setPickReact] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [poll, setPoll] = useState(null);          // {id, q, opts, votes:{choiceIdx:count}, mine}
  const [showPollForm, setShowPollForm] = useState(false);
  const attendanceRef = useRef({});                // name -> {joined, left}
  const [knocks, setKnocks] = useState([]);        // waiting guests (host view)
  const [breakouts, setBreakouts] = useState([]);  // open breakout room names
  const [showBO, setShowBO] = useState(false);
  const boChannelRef = useRef(null);
  const inBreakout = !!(activeRoom && mainRoom && activeRoom !== mainRoom);

  const transcriptRef = useRef([]);   // accumulated finals for notes
  const egressIdRef = useRef(null);
  const recRef = useRef(null);        // SpeechRecognition instance

  const flash = useCallback((m) => { setToast(m); setTimeout(() => setToast(''), 2600); }, []);
  const fmtTime = (sec) => String(Math.floor(sec / 60)).padStart(2, '0') + ':' + String(sec % 60).padStart(2, '0');
  function inviteMember(m) {
    setInviteSent(prev => ({ ...prev, [m.email]: true }));
    fetch('/api/notify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emails: [m.email], event: 'call', title: myName + ' is inviting you to the Team Room', body: 'Tap to join the call', url: '/?room=' + encodeURIComponent(roomName), requireInteraction: true }) }).catch(() => {});
  }

  // Watch whether the LiveKit chat panel is open, so toolbars dodge it.
  useEffect(() => {
    const id = setInterval(() => { try { setChatOpen(!!document.querySelector('.sanoire-lk .lk-chat')); } catch (_e) {} }, 600);
    return () => clearInterval(id);
  }, []);

  const pushCaption = useCallback((c) => {
    setCaptions(prev => [...prev.filter(p => p.init !== c.init), c].slice(-4));
  }, []);

  // ── Inbound data: captions, reactions, hands, persisted chat ──
  useEffect(() => {
    if (!room) return;
    const onData = (payload, participant, _kind, topic) => {
      try {
        const text = dec.decode(payload);
        if (topic === 'sanoire-cc') {
          const c = JSON.parse(text); pushCaption(c);
          transcriptRef.current.push({ name: c.name, text: c.text, ts: c.ts });
        } else if (topic === 'sanoire-react') {
          const r = JSON.parse(text); const id = Math.random().toString(16).slice(2);
          setReacts(prev => [...prev, { id, emoji: r.emoji, name: r.name }]);
          setTimeout(() => setReacts(prev => prev.filter(x => x.id !== id)), 3200);
        } else if (topic === 'sanoire-hand') {
          const h = JSON.parse(text);
          setHands(prev => { const n = { ...prev }; if (h.raised) n[h.init] = h.name; else delete n[h.init]; return n; });
        } else if (topic === 'sanoire-cmd') {
          const c = JSON.parse(text);
          if (c.action === 'mute') { try { localParticipant && localParticipant.setMicrophoneEnabled(false); } catch (_e) {} flash('Muted by host'); }
          else if (c.action === 'lowerhands') { setHandUp(false); setHands({}); }
        } else if (topic === 'sanoire-poll') {
          const p = JSON.parse(text); setPoll({ id: p.id, q: p.q, opts: p.opts, votes: {}, mine: null });
        } else if (topic === 'sanoire-vote') {
          const v = JSON.parse(text);
          setPoll(prev => (prev && prev.id === v.id) ? { ...prev, votes: { ...prev.votes, [v.choice]: (prev.votes[v.choice] || 0) + 1 } } : prev);
        } else if (topic === 'lk-chat-topic' && supabase) {
          let msg = ''; try { const j = JSON.parse(text); msg = j.message || j.text || ''; } catch (_e) { msg = text; }
          if (msg) supabase.from('call_chat_messages').insert({ session_id: sessionId || null, room_name: roomName, author: participant?.name || 'Member', author_initials: (participant?.name || 'M').slice(0, 2).toUpperCase(), body: msg, kind: 'chat' }).then(() => {}, () => {});
        }
      } catch (_e) {}
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => { try { room.off(RoomEvent.DataReceived, onData); } catch (_e) {} };
  }, [room, supabase, sessionId, roomName, pushCaption]);

  // ── Persist my own outgoing chat too (DataReceived only fires for remotes) ──
  // (LiveKit chat send is captured on remotes; local copy saved via chat hook below is optional.)

  // ── Live captions via browser SpeechRecognition ──
  useEffect(() => {
    const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!ccOn || !SR) { if (!SR && ccOn) { flash('Captions need Chrome'); setCcOn(false); } return; }
    let stopped = false;
    const rec = new SR();
    recRef.current = rec;
    rec.continuous = true; rec.interimResults = true; rec.lang = (navigator.language || 'en-US');
    rec.onresult = (e) => {
      let finalTxt = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalTxt += e.results[i][0].transcript;
      }
      finalTxt = finalTxt.trim();
      if (finalTxt) {
        const c = { init: myInit, name: myName, text: finalTxt, ts: new Date().toISOString() };
        pushCaption(c); transcriptRef.current.push({ name: myName, text: finalTxt, ts: c.ts });
        publish(room, 'sanoire-cc', c);
      }
    };
    rec.onend = () => { if (!stopped) { try { rec.start(); } catch (_e) {} } };
    try { rec.start(); } catch (_e) {}
    return () => { stopped = true; try { rec.stop(); } catch (_e) {} recRef.current = null; };
  }, [ccOn, room, myInit, myName, pushCaption, flash]);

  // ── Camera effects: background blur or Sanoire virtual background ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pub = localParticipant && localParticipant.getTrackPublication(Track.Source.Camera);
        const track = pub && pub.track;
        if (!track) return;
        if (camFx === 'blur') { const mod = await import('@livekit/track-processors'); if (cancelled) return; await track.setProcessor(mod.BackgroundBlur(12)); }
        else if (camFx === 'bg' && SANOIRE_BG) { const mod = await import('@livekit/track-processors'); if (cancelled) return; await track.setProcessor(mod.VirtualBackground(SANOIRE_BG)); }
        else if (track.getProcessor && track.getProcessor()) { await track.stopProcessor(); }
      } catch (e) { if (camFx !== 'none') { flash('Effect unavailable'); setCamFx('none'); } }
    })();
    return () => { cancelled = true; };
  }, [camFx, localParticipant, flash]);

  // ── Noise cancellation (Krisp) on the microphone ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pub = localParticipant && localParticipant.getTrackPublication(Track.Source.Microphone);
        const track = pub && pub.track;
        if (!track) { if (noiseOn) { flash('Turn your mic on first'); setNoiseOn(false); } return; }
        if (noiseOn) {
          const mod = await import('@livekit/krisp-noise-filter');
          if (cancelled) return;
          if (mod.isKrispNoiseFilterSupported && !mod.isKrispNoiseFilterSupported()) { flash('Noise cancel not supported here'); setNoiseOn(false); return; }
          await track.setProcessor(mod.KrispNoiseFilter());
        } else if (track.getProcessor && track.getProcessor()) { await track.stopProcessor(); }
      } catch (e) { if (noiseOn) { flash('Noise cancellation unavailable'); setNoiseOn(false); } }
    })();
    return () => { cancelled = true; };
  }, [noiseOn, localParticipant, flash]);

  // ── Call timer ──
  useEffect(() => { const id = setInterval(() => setElapsed(e => e + 1), 1000); return () => clearInterval(id); }, []);

  // ── Load team members when the invite panel opens ──
  useEffect(() => {
    if (!invitePanel || !supabase) return;
    supabase.from('team_members').select('name,email,initials,color,text_color').eq('active', true).then(({ data }) => setInviteMembers(data || [])).catch(() => {});
  }, [invitePanel, supabase]);

  // ── Raise hand broadcast ──
  useEffect(() => { if (room) publish(room, 'sanoire-hand', { init: myInit, name: myName, raised: handUp }); }, [handUp, room, myInit, myName]);

  // ── Recording (Egress) ──
  async function toggleRecord() {
    if (recBusy) return;
    setRecBusy(true);
    try {
      if (!recOn) {
        const r = await fetch('/api/livekit-egress', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'start', room: roomName }) });
        const d = await r.json();
        if (!r.ok) { flash(d.error === 'recording_storage_unconfigured' ? 'Recording storage not set up' : (d.message || 'Recording failed')); }
        else { egressIdRef.current = d.egressId; setRecOn(true); flash('Recording started');
          if (supabase && sessionId) supabase.from('call_video_sessions').update({ recording_id: d.egressId, recording_url: d.recording_url || null }).eq('id', sessionId).then(() => {}, () => {}); }
      } else {
        await fetch('/api/livekit-egress', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'stop', egressId: egressIdRef.current }) });
        setRecOn(false); flash('Recording stopped');
      }
    } catch (_e) { flash('Recording error'); }
    setRecBusy(false);
  }

  // ── Attendance: record who joined/left ──
  useEffect(() => {
    if (!room) return;
    const mark = (p, key) => { const n = p && (p.name || p.identity); if (!n) return; attendanceRef.current[n] = { ...(attendanceRef.current[n] || {}), [key]: new Date().toISOString() }; };
    try { (room.remoteParticipants ? Array.from(room.remoteParticipants.values()) : []).forEach(p => mark(p, 'joined')); } catch (_e) {}
    try { mark(localParticipant, 'joined'); } catch (_e) {}
    const onJoin = (p) => mark(p, 'joined');
    const onLeave = (p) => mark(p, 'left');
    room.on(RoomEvent.ParticipantConnected, onJoin);
    room.on(RoomEvent.ParticipantDisconnected, onLeave);
    return () => { try { room.off(RoomEvent.ParticipantConnected, onJoin); room.off(RoomEvent.ParticipantDisconnected, onLeave); } catch (_e) {} };
  }, [room, localParticipant]);

  // ── Host: waiting-room knocks (server-enforced) ──
  useEffect(() => {
    if (!supabase || !roomName) return;
    let active = true;
    const load = async () => {
      try { const { data } = await supabase.from('call_knocks').select('id, guest_name, status').eq('room_name', roomName).eq('status', 'waiting').order('created_at'); if (active) setKnocks(data || []); } catch (_e) {}
    };
    load();
    let channel;
    try {
      channel = supabase.channel('knocks-' + roomName)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'call_knocks', filter: 'room_name=eq.' + roomName }, load)
        .subscribe();
    } catch (_e) {}
    return () => { active = false; try { if (channel) supabase.removeChannel(channel); } catch (_e) {} };
  }, [supabase, roomName]);

  async function decideKnock(id, action) {
    setKnocks(prev => prev.filter(k => k.id !== id));
    try {
      const { data } = await supabase.auth.getSession();
      const tk = data && data.session && data.session.access_token;
      await fetch('/api/livekit-admit', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(tk ? { Authorization: 'Bearer ' + tk } : {}) }, body: JSON.stringify({ knockId: id, action }) });
    } catch (_e) {}
  }

  // ── Breakout rooms (Supabase broadcast reaches members across LiveKit rooms) ──
  useEffect(() => {
    if (!supabase || !mainRoom) return;
    const ch = supabase.channel('breakout-' + mainRoom, { config: { broadcast: { self: false } } });
    ch.on('broadcast', { event: 'bo' }, ({ payload }) => {
      if (!payload) return;
      if (payload.action === 'open') setBreakouts(payload.rooms || []);
      else if (payload.action === 'return') { setBreakouts([]); if (onSwitchRoom && mainRoom) onSwitchRoom(mainRoom); }
      else if (payload.action === 'close') setBreakouts([]);
    }).subscribe();
    boChannelRef.current = ch;
    return () => { try { supabase.removeChannel(ch); } catch (_e) {} };
  }, [supabase, mainRoom, onSwitchRoom]);

  function sendBO(action, rooms) { try { boChannelRef.current && boChannelRef.current.send({ type: 'broadcast', event: 'bo', payload: { action, rooms: rooms || [] } }); } catch (_e) {} }
  function openBreakouts(n) { const rooms = Array.from({ length: n }, (_, i) => mainRoom + '~b' + (i + 1)); setBreakouts(rooms); setShowBO(false); sendBO('open', rooms); flash('Breakouts opened'); }
  function returnAll() { sendBO('return', []); setBreakouts([]); setShowBO(false); if (inBreakout && onSwitchRoom) onSwitchRoom(mainRoom); }

  // ── On disconnect: save transcript + generate AI notes ──
  useEffect(() => {
    if (!room) return;
    const onDisc = () => {
      const tr = transcriptRef.current;
      if (!tr.length) return;
      if (mainRoom && roomName && roomName !== mainRoom) return; // skip breakout segments

      try {
        const attendees = Object.keys(attendanceRef.current);
        if (supabase && sessionId) supabase.from('call_video_sessions').update({ transcript: tr, participants: attendees.length ? attendees : null }).eq('id', sessionId).then(() => {}, () => {});
        fetch('/api/meeting-notes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ session_id: sessionId, room: roomName, transcript: tr }) }).catch(() => {});
      } catch (_e) {}
    };
    room.on(RoomEvent.Disconnected, onDisc);
    return () => { try { room.off(RoomEvent.Disconnected, onDisc); } catch (_e) {} };
  }, [room, supabase, sessionId, roomName]);

  // ── UI ──
  const btn = (active, danger) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    height: 38, padding: '0 12px', borderRadius: 10, cursor: 'pointer', fontFamily: TXT,
    fontSize: 12.5, fontWeight: 600, border: '1px solid ' + (active ? 'transparent' : 'rgba(255,255,255,0.14)'),
    background: danger && active ? '#c62828' : (active ? '#314F5C' : 'rgba(255,255,255,0.08)'),
    color: active ? '#E0F5FC' : '#cfe0ea', backdropFilter: 'blur(6px)', transition: 'all .15s', whiteSpace: 'nowrap',
  });
  const ibtn = (active) => ({ ...btn(active), height: 34, minWidth: 38, padding: '0 10px', justifyContent: 'center', gap: 4 });
  const menuItem = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#eef5f9', fontFamily: TXT, fontSize: 12.5, fontWeight: 600 };
  const handList = Object.values(hands);

  return (
    <>
      {/* Control strip — top-right (compact toolbar + overflow menu) */}
      <div style={{ position: 'absolute', top: 12, right: chatOpen ? 360 : 12, zIndex: 30, transition: 'right .2s ease', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap', maxWidth: 'calc(100% - 24px)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, height: 34, padding: '0 10px', borderRadius: 10, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', color: '#cfe0ea', fontFamily: TXT, fontSize: 12.5, fontWeight: 600 }}><IconClock />{fmtTime(elapsed)}</span>
        {inBreakout && <button style={btn(true)} onClick={() => onSwitchRoom && onSwitchRoom(mainRoom)} title="Back to main room">← Main</button>}
        <div style={{ position: 'relative' }}>
          <button style={ibtn(pickReact)} onClick={() => setPickReact(v => !v)} title="Reactions"><IconSmile /></button>
          {pickReact && (
            <div style={{ position: 'absolute', top: 42, right: 0, display: 'flex', gap: 4, padding: 6, borderRadius: 12, background: 'rgba(11,22,32,0.96)', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 8px 28px rgba(0,0,0,0.4)' }}>
              {REACTIONS.map(em => (
                <button key={em} onClick={() => { publish(room, 'sanoire-react', { emoji: em, name: myName }); const id = Math.random().toString(16).slice(2); setReacts(prev => [...prev, { id, emoji: em, name: myName }]); setTimeout(() => setReacts(prev => prev.filter(x => x.id !== id)), 3200); setPickReact(false); }} style={{ fontSize: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 8 }}>{em}</button>
              ))}
            </div>
          )}
        </div>
        <button style={ibtn(handUp)} onClick={() => setHandUp(v => !v)} title={handUp ? 'Lower hand' : 'Raise hand'}><IconHand /></button>
        <button style={ibtn(ccOn)} onClick={() => setCcOn(v => !v)} title="Live captions">CC</button>
        <button style={ibtn(camFx === 'blur')} onClick={() => setCamFx(f => f === 'blur' ? 'none' : 'blur')} title="Background blur">Blur</button>
        {recOn && <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '0 8px', height: 34, borderRadius: 10, background: 'rgba(198,40,40,0.9)', color: '#fff', fontFamily: TXT, fontSize: 12, fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />REC</span>}
        <div style={{ position: 'relative' }}>
          <button style={ibtn(moreOpen || showPollForm || showBO || breakouts.length > 0)} onClick={() => setMoreOpen(v => !v)} title="More"><IconDots /></button>
          {moreOpen && (
            <div style={{ position: 'absolute', top: 42, right: 0, width: 212, padding: 6, borderRadius: 12, background: 'rgba(11,22,32,0.97)', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 10px 30px rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button style={menuItem} onClick={() => { setInvitePanel(true); setMoreOpen(false); }}><IconUserPlus /> Invite people</button>
              <button style={menuItem} onClick={() => { toggleRecord(); setMoreOpen(false); }} disabled={recBusy}><span style={{ width: 9, height: 9, borderRadius: '50%', background: recOn ? '#fff' : '#ef5350' }} />{recOn ? 'Stop recording' : 'Record call'}{recOn ? <span style={{ marginLeft: 'auto', color: '#7fd0a0', fontWeight: 700 }}>✓</span> : null}</button>
              <button style={menuItem} onClick={() => { setNoiseOn(v => !v); setMoreOpen(false); }}><IconSparkle /> Noise cancellation{noiseOn ? <span style={{ marginLeft: 'auto', color: '#7fd0a0', fontWeight: 700 }}>✓</span> : null}</button>
              <button style={menuItem} onClick={() => { setCamFx(f => f === 'bg' ? 'none' : 'bg'); setMoreOpen(false); }}><IconImage /> Virtual background{camFx === 'bg' ? <span style={{ marginLeft: 'auto', color: '#7fd0a0', fontWeight: 700 }}>✓</span> : null}</button>
              <button style={menuItem} onClick={() => { publish(room, 'sanoire-cmd', { action: 'mute' }); flash('Asked everyone to mute'); setMoreOpen(false); }}><IconMicOff /> Mute everyone</button>
              <button style={menuItem} onClick={() => { publish(room, 'sanoire-cmd', { action: 'lowerhands' }); setHands({}); setHandUp(false); flash('Lowered all hands'); setMoreOpen(false); }}><IconHand /> Lower all hands</button>
              <button style={menuItem} onClick={() => { setShowPollForm(true); setShowBO(false); setMoreOpen(false); }}><IconPoll /> Start a poll</button>
              <button style={menuItem} onClick={() => { setShowBO(true); setShowPollForm(false); setMoreOpen(false); }}><IconGrid /> Breakout rooms</button>
            </div>
          )}
        </div>
      </div>

      {/* Raised hands badge — top-left */}
      {handList.length > 0 && (
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 30, display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 999, background: 'rgba(159,126,79,0.92)', color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: TXT, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
          ✋ {handList.length === 1 ? handList[0] : handList.length + ' hands raised'}
        </div>
      )}

      {/* Waiting-room knocks — host admit/deny */}
      {knocks.length > 0 && (
        <div style={{ position: 'absolute', top: handList.length > 0 ? 56 : 12, left: 12, zIndex: 31, width: 250, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {knocks.map(k => (
            <div key={k.id} style={{ background: 'rgba(11,22,32,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
              <div style={{ color: '#eef5f9', fontFamily: TXT, fontSize: 12.5 }}><b>{k.guest_name}</b> wants to join</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => decideKnock(k.id, 'admit')} style={{ flex: 1, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: TXT, fontSize: 12, fontWeight: 700, background: '#314F5C', color: '#E0F5FC' }}>Admit</button>
                <button onClick={() => decideKnock(k.id, 'deny')} style={{ flex: 1, height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,0.16)', cursor: 'pointer', fontFamily: TXT, fontSize: 12, fontWeight: 600, background: 'transparent', color: '#cfe0ea' }}>Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Floating reactions */}
      <div style={{ position: 'absolute', bottom: 90, left: 0, right: 0, zIndex: 28, pointerEvents: 'none', display: 'flex', justifyContent: 'center', gap: 18 }}>
        {reacts.map(r => (
          <div key={r.id} style={{ fontSize: 38, animation: 'sanoireFloat 3s ease-out forwards', textAlign: 'center' }}>
            {r.emoji}<div style={{ fontSize: 10, color: '#fff', fontFamily: TXT, opacity: 0.8 }}>{r.name}</div>
          </div>
        ))}
      </div>

      {/* Captions overlay */}
      {ccOn && captions.length > 0 && (
        <div style={{ position: 'absolute', bottom: 86, left: '50%', transform: 'translateX(-50%)', zIndex: 29, width: 'min(720px, 92%)', display: 'flex', flexDirection: 'column', gap: 4, pointerEvents: 'none' }}>
          {captions.slice(-3).map((c, i) => (
            <div key={i} style={{ alignSelf: 'center', background: 'rgba(11,22,32,0.82)', color: '#eef5f9', padding: '6px 13px', borderRadius: 10, fontSize: 14, fontFamily: TXT, lineHeight: 1.4, maxWidth: '100%' }}>
              <b style={{ color: '#AFE1F6', fontWeight: 700 }}>{c.name}:</b> {c.text}
            </div>
          ))}
        </div>
      )}

      {/* Poll create form (host) */}
      {showPollForm && (
        <div style={{ position: 'absolute', top: 60, right: chatOpen ? 360 : 12, zIndex: 31, width: 260, background: 'rgba(11,22,32,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.45)' }}>
          <div style={{ color: '#eef5f9', fontFamily: TXT, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Quick poll</div>
          <input id="lk-poll-q" placeholder="Question" style={{ width: '100%', boxSizing: 'border-box', marginBottom: 6, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)', color: '#eef5f9', fontFamily: TXT, fontSize: 12.5, outline: 'none' }} />
          <input id="lk-poll-o" placeholder="Options, comma-separated" style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)', color: '#eef5f9', fontFamily: TXT, fontSize: 12.5, outline: 'none' }} />
          <button onClick={() => { const q = (document.getElementById('lk-poll-q') || {}).value || ''; const opts = ((document.getElementById('lk-poll-o') || {}).value || '').split(',').map(s => s.trim()).filter(Boolean); if (!q || opts.length < 2) { flash('Add a question + 2 options'); return; } const p = { id: Math.random().toString(16).slice(2), q, opts, votes: {}, mine: null }; setPoll(p); publish(room, 'sanoire-poll', { id: p.id, q, opts }); setShowPollForm(false); }} style={{ ...btn(true), width: '100%', height: 34 }}>Start poll</button>
        </div>
      )}

      {/* Active poll (everyone) */}
      {poll && (
        <div style={{ position: 'absolute', bottom: 90, right: chatOpen ? 360 : 12, zIndex: 31, width: 240, background: 'rgba(11,22,32,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.45)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ color: '#eef5f9', fontFamily: TXT, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{poll.q}</div>
            <button onClick={() => setPoll(null)} style={{ background: 'none', border: 'none', color: '#8fa9b8', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
          {poll.opts.map((o, i) => {
            const count = poll.votes[i] || 0; const total = Object.values(poll.votes).reduce((a, b) => a + b, 0) || 1; const pct = Math.round((count / total) * 100);
            return (
              <button key={i} disabled={poll.mine !== null} onClick={() => { setPoll(prev => ({ ...prev, mine: i, votes: { ...prev.votes, [i]: (prev.votes[i] || 0) + 1 } })); publish(room, 'sanoire-vote', { id: poll.id, choice: i }); }} style={{ width: '100%', textAlign: 'left', position: 'relative', overflow: 'hidden', marginBottom: 6, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)', color: '#eef5f9', fontFamily: TXT, fontSize: 12.5, cursor: poll.mine === null ? 'pointer' : 'default' }}>
                <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: pct + '%', background: poll.mine === i ? 'rgba(49,79,92,0.85)' : 'rgba(255,255,255,0.08)', transition: 'width .3s' }} />
                <span style={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}><span>{o}</span><span style={{ opacity: 0.8 }}>{pct}%</span></span>
              </button>
            );
          })}
        </div>
      )}

      {/* Breakout form (host) */}
      {showBO && (
        <div style={{ position: 'absolute', top: 60, right: chatOpen ? 360 : 12, zIndex: 31, width: 230, background: 'rgba(11,22,32,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.45)' }}>
          <div style={{ color: '#eef5f9', fontFamily: TXT, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Breakout rooms</div>
          <div style={{ color: '#8fa9b8', fontFamily: TXT, fontSize: 11, marginBottom: 10 }}>Split into how many rooms?</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[2, 3, 4].map(n => <button key={n} onClick={() => openBreakouts(n)} style={{ ...btn(false), flex: 1, height: 34, justifyContent: 'center' }}>{n}</button>)}
          </div>
          {breakouts.length > 0 && <button onClick={returnAll} style={{ ...btn(false), width: '100%', height: 32, justifyContent: 'center' }}>Return everyone to main</button>}
        </div>
      )}

      {/* Breakout chooser (while still in the main room) */}
      {breakouts.length > 0 && !inBreakout && (
        <div style={{ position: 'absolute', bottom: 130, left: '50%', transform: 'translateX(-50%)', zIndex: 31, background: 'rgba(11,22,32,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: '11px 14px', boxShadow: '0 10px 30px rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span style={{ color: '#eef5f9', fontFamily: TXT, fontSize: 12.5, fontWeight: 600 }}>Breakouts open:</span>
          {breakouts.map((r, i) => <button key={r} onClick={() => onSwitchRoom && onSwitchRoom(r)} style={{ ...btn(false), height: 32, justifyContent: 'center' }}>Join room {i + 1}</button>)}
        </div>
      )}

      {/* Invite people */}
      {invitePanel && (
        <div style={{ position: 'absolute', top: 60, right: chatOpen ? 360 : 12, zIndex: 32, width: 252, maxHeight: '70%', overflowY: 'auto', background: 'rgba(11,22,32,0.98)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 12, boxShadow: '0 12px 34px rgba(0,0,0,0.5)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ color: '#eef5f9', fontFamily: TXT, fontSize: 13, fontWeight: 700 }}>Invite to call</span>
            <button onClick={() => setInvitePanel(false)} style={{ background: 'none', border: 'none', color: '#8fa9b8', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
          {inviteMembers.filter(m => m.email && m.email !== (me && me.email)).map(m => (
            <div key={m.email} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 4px' }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: m.color || '#6F8FA7', color: m.text_color || '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{m.initials || (m.name || '?').slice(0, 2).toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}><div style={{ color: '#eef5f9', fontFamily: TXT, fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div></div>
              <button onClick={() => inviteMember(m)} disabled={!!inviteSent[m.email]} style={{ ...btn(!!inviteSent[m.email]), height: 30 }}>{inviteSent[m.email] ? 'Sent' : 'Ring'}</button>
            </div>
          ))}
          {inviteMembers.length === 0 && <div style={{ color: '#8fa9b8', fontFamily: TXT, fontSize: 12, padding: 8 }}>Loading team…</div>}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)', zIndex: 31, background: 'rgba(11,22,32,0.95)', color: '#E0F5FC', padding: '8px 16px', borderRadius: 10, fontSize: 12.5, fontFamily: TXT, border: '1px solid rgba(255,255,255,0.12)' }}>{toast}</div>
      )}

      <style>{`@keyframes sanoireFloat { 0%{ transform: translateY(0) scale(.8); opacity:0 } 15%{ opacity:1 } 100%{ transform: translateY(-120px) scale(1.1); opacity:0 } }`}</style>
    </>
  );
}
